import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { logOut } from "./globalSlice";
import Api from "../api/api";
import { AppDispatch, RootState } from "./store";
import { addEntityFetchReducers, EntityState } from "./entity";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { Activation, License, Order, Product } from "../api/models";
import { useFetchableState } from "./fetchableState";
import { switchSubscriptionThunk } from "./subscriptionSlice";
import { redeemCodeThunk, redeemResellerCodeThunk, redemptionsThunk, redemptionThunk } from "./redemptionsSlice";
import { revokeActivationThunk } from "./activationsSlice";
import { claimRewardThunk } from "./storeSlice";
import { libraryThunk, transferEntitlementThunk } from "./libraryThunk";

interface LicensesState extends EntityState<License> {
    collection: {
        byProduct: {
            [productId: string]: {
                ids: string[],
            },
        },
        currentRequestId?: string
        lastFetched?: number
    },
    debug: string
}

const initialState: LicensesState = {
    entities: {},
    collection: {
        byProduct: {},
        currentRequestId: undefined,
        lastFetched: undefined
    },
    debug: "licenses state"
}

export const licensesSlice = createSlice({
    name: 'licenses',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            License,
            { license: License, product: Product, activations: Activation[], order?: Order },
            { id: string, accessToken: string }
        >(
            builder,
            (payload) => payload.license,
            licenseThunk
        )
        builder.addCase(licenseCollectionThunk.pending, (state, action) => {
            const collection = state.collection
            if (collection.currentRequestId)
                return
            collection.currentRequestId = action.meta.requestId
            licenseCollectionReducerIntercept(licenseCollectionThunk.pending, state, action)
        })
        builder.addCase(licenseCollectionThunk.fulfilled, (state, action) => {
            if (state.collection.currentRequestId !== action.meta.requestId)
                return
            state.collection.currentRequestId = undefined
            state.collection.lastFetched = Date.now()
            state.collection.byProduct = {}
            action.payload.licenses.forEach(license => {
                if (state.collection.byProduct[license.productId]) {
                    state.collection.byProduct[license.productId].ids.push(license.id)
                } else {
                    state.collection.byProduct[license.productId] = {
                        ids: [license.id]
                    }
                }
            })
            licenseCollectionReducerIntercept(licenseCollectionThunk.fulfilled, state, action)
        })
        builder.addCase(licenseCollectionThunk.rejected, (state, action) => {
            if (state.collection.currentRequestId !== action.meta.requestId)
                return
            state.collection.currentRequestId = undefined
            licenseCollectionReducerIntercept(licenseCollectionThunk.rejected, state, action)
        })
        builder.addCase(switchSubscriptionThunk.fulfilled, (state, action) => {
            // licenses might have changed after subscription update, we reset the collection so that
            // it will be refetched on next access and the store will be consistent with the backend
            state.collection.lastFetched = undefined
            state.collection.currentRequestId = undefined
            state.collection.byProduct = {}
        })
        // the moved licenses are another account's now — drop them from the
        // entity cache (nothing else evicts a license) and let the collection
        // refetch, so the Dashboard and /licenses/:id don't keep showing them
        builder.addCase(transferEntitlementThunk.fulfilled, (state, action) => {
            for (const licenseId of action.payload.movedLicenseIds)
                delete state.entities[licenseId]
            state.collection.lastFetched = undefined
            state.collection.currentRequestId = undefined
            state.collection.byProduct = {}
        })
        builder.addCase(redemptionThunk.fulfilled, (state, action) => {
            const { licenses } = action.payload
            licenses.forEach(license => {
                state.entities[license.id] = {
                    data: license,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(redemptionsThunk.fulfilled, (state, action) => {
            const { licenses } = action.payload
            licenses.forEach(license => {
                state.entities[license.id] = {
                    data: license,
                    lastFetched: Date.now()
                }
            })
        })
        // the library carries every license of the user — hydrate the
        // entities so the click-through to /licenses/:id is instant
        builder.addCase(libraryThunk.fulfilled, (state, action) => {
            const { licenses } = action.payload
            licenses.forEach(license => {
                if (state.entities[license.id]?.currentRequestId !== undefined)
                    return
                state.entities[license.id] = {
                    data: license,
                    lastFetched: Date.now()
                }
            })
        })

        // a code redemption and a reseller-code redemption side-load the same entities
        const onCodeRedeemed = (state: LicensesState, action: { payload: { licenses: License[] } }) => {
            const { licenses } = action.payload
            licenses.forEach(license => {
                // add license to perProduct collection if not already there
                if (state.collection.byProduct[license.productId]) {
                    if (!state.collection.byProduct[license.productId].ids.includes(license.id)) {
                        state.collection.byProduct[license.productId].ids.push(license.id)
                    }
                } else {
                    state.collection.byProduct[license.productId] = {
                        ids: [license.id]
                    }
                }
                state.entities[license.id] = {
                    data: license,
                    lastFetched: Date.now()
                }
            })
        }
        builder.addCase(redeemCodeThunk.fulfilled, onCodeRedeemed)
        builder.addCase(redeemResellerCodeThunk.fulfilled, onCodeRedeemed)

        builder.addCase(claimRewardThunk.fulfilled, (state, action) => {
            const { licenses } = action.payload
            licenses.forEach(license => {
                // add license to perProduct collection if not already there
                if (state.collection.byProduct[license.productId]) {
                    if (!state.collection.byProduct[license.productId].ids.includes(license.id)) {
                        state.collection.byProduct[license.productId].ids.push(license.id)
                    }
                } else {
                    state.collection.byProduct[license.productId] = {
                        ids: [license.id]
                    }
                }
                state.entities[license.id] = {
                    data: license,
                    lastFetched: Date.now()
                }
            })
        })

        builder.addCase(revokeActivationThunk.fulfilled, (state, action) => {
            const { license } = action.payload
            state.entities[license.id] = {
                data: license,
                lastFetched: Date.now()
            }
        })

        builder.addCase(logOut, (state: LicensesState) => {
            state = initialState;
        })
    },
})


// GET licenses/:licenseId

export const licenseThunk = createAsyncThunk(
    'licenses/fetchLicense',
    // async ({ accessToken }: { accessToken: string }, { getState, requestId }) => {
    // const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
    // const { currentRequestId } = state.licenses;
    // if (currentRequestId !== requestId)
    //     throw new Error("Request outdated");
    async ({ id, accessToken }: { id: string, accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const { currentRequestId } = state.licenses.entities[id] || {};
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.getLicense({ licenseId: id, accessToken })
    }
)

export function useLicense(id: string, accessToken: string) {
    return useFetchableState<License>({
        selector: (state: RootState) => state.licenses.entities[id],
        action: licenseThunk({ id, accessToken })
    })
}


// GET licenses

export const licenseCollectionThunk = createAsyncThunk(
    'licenses/fetchLicenses',
    async ({ accessToken }: { accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState
        const { currentRequestId } = state.licenses.collection;
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.getLicenses({ accessToken })
    }
)

const licenseCollectionSelector = (state: RootState) => state.licenses.collection
// const licenseCollectionRefreshAction = { type: "licenses/refresh", payload: null } as any
const licenseCollectionReducerIntercept = (
    type: any,
    state: any,
    action: PayloadAction<any>
) => {
    if (type === licenseCollectionThunk.fulfilled) {
        const { licenses } = action.payload
        licenses.forEach((license: License) => {
            const entity = state.entities[license.id]
            if (entity?.currentRequestId !== undefined)
                return
            state.entities[license.id] = {
                data: license,
                lastFetched: Date.now()
            }
        })
        // in case some licenses were not included in the response for some reason, we want to keep them in the store until they are explicitly refreshed or invalidated
        // TODO: what to do with products and activations?!?!
    }
}

export function useLicenseCollection(accessToken: string, cacheExpiry = 60 * 1000) {
    const dispatch = useDispatch<AppDispatch>()
    const collection = useSelector(licenseCollectionSelector)
    const lastFetched = collection.lastFetched
    const [needRefresh, setNeedRefresh] = useState(!lastFetched || Date.now() - lastFetched > cacheExpiry)

    const refresh = () => {
        dispatch(licenseCollectionThunk({ accessToken }))
    }

    useEffect(() => {
        if (needRefresh) {
            dispatch(licenseCollectionThunk({ accessToken }))
            setNeedRefresh(false);
        }
    }, [needRefresh, dispatch, accessToken])

    // const { lastFetched: _, ...rest } = collection || {};

    return {
        collection,
        refresh
    };
}

export default licensesSlice.reducer